"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRouter, useSearchParams } from "next/navigation";
import axios from "axios";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import api from "@/lib/axios";
import { useUser } from "@/context/UserContext";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const { refreshUser } = useUser();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  // Auto-verify when OTP is complete
  useEffect(() => {
    if (otp.length === 6) {
      verifyOtp();
    }
  }, [otp]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await api.post("/auth/magic-link", values);
      setShowOtp(true);
      setOtpError(null);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        if (data?.errors?.email) {
          form.setError("email", { message: data.errors.email[0] });
        }
      }
    }
  };

  const verifyOtp = async () => {
    setIsVerifying(true);
    setOtpError(null);

    try {
      const response = await api.post("/auth/verify-otp", {
        email: form.getValues("email"),
        code: otp,
      });

      if (response.data.success) {
        await refreshUser();
        const returnUrl = searchParams.get("returnUrl");
        router.push(returnUrl || "/campaigns");
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        setOtpError(data?.message || "Invalid verification code");
      }
      setOtp("");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      const returnUrl = searchParams.get("returnUrl");
      if (returnUrl) {
        localStorage.setItem("oauth_return_url", returnUrl);
      }

      const response = await api.get("/oauth/redirect/google");
      const { url } = response.data;

      if (typeof window !== "undefined") {
        window.location.href = url;
      }
    } catch (error) {
      console.error("Failed to get OAuth redirect URL:", error);
      setIsGoogleLoading(false);
    }
  };

  if (showOtp) {
    return (
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Verify your email</CardTitle>
          <CardDescription>
            Enter the 6-digit code sent to {form.getValues("email")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 justify-center">
            <InputOTP
              maxLength={6}
              value={otp}
              onChange={(value) => {
                setOtp(value);
                setOtpError(null);
              }}
              disabled={isVerifying}
              render={({ slots }) => (
                <InputOTPGroup className="gap-2 justify-center">
                  {slots.map((slot, idx) => (
                    <InputOTPSlot
                      key={idx}
                      {...slot}
                      className={`h-10 w-10 sm:h-10 sm:w-10 text-xl border-2 font-medium shadow-md rounded-md 
                        ${isVerifying ? "opacity-50 cursor-not-allowed" : ""}
                        ${
                          !!otpError
                            ? "border-red-500"
                            : "border-gray-200 hover:border-gray-300 focus:border-gray-300 focus:ring-2 focus:ring-gray-200"
                        }`}
                    />
                  ))}
                </InputOTPGroup>
              )}
            />
            {isVerifying && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Verifying code...
              </div>
            )}
            {otpError && (
              <p className="text-sm text-destructive text-center">{otpError}</p>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setShowOtp(false);
                setOtp("");
                setOtpError(null);
              }}
              disabled={isVerifying}
            >
              Back to sign in
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>Sign in to your account to continue</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-6">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <svg
                    className="w-5 h-5 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                      fill="currentColor"
                    />
                  </svg>
                )}
                Continue with Google
              </Button>

              <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                <span className="relative z-10 bg-background px-2 text-muted-foreground">
                  Or continue with email
                </span>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  {...form.register("email")}
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  disabled={form.formState.isSubmitting}
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Continue with Email"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary">
        By clicking continue, you agree to our <a href="#">Terms of Service</a>{" "}
        and <a href="#">Privacy Policy</a>.
      </div>
    </div>
  );
}
